import { Injectable, OnDestroy, inject } from '@angular/core';

import { TCreatedPdf } from 'pdfmake/build/pdfmake';
import { Subject, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

import { PAYMENT_TAB_ID } from '@shared-constants';
import { ModalVariant } from '@shared-enums';
import { Note } from '@shared-models';
import { DOCUMENT_STATE } from '@shared-utils';

import { IInvoicePayments } from 'src/app/@interfaces/payments.interface';
import { IQuotation } from 'src/app/@interfaces/quotation.interface';
import { PortfolioService } from 'src/app/@pages/portfolio/portfolio.service';
import { QuotationsService } from 'src/app/@pages/quotations/quotations.service';
import { PaymentsPdfService } from 'src/app/@shared/components/dialog/crud-quotation/payments/payments-pdf.service';
import { PdfGeneratorService } from 'src/app/@shared/components/pdf-generator/pdf-generator.service';

import { PaymentModalComponent } from '../../../features/commercial/shared/components/payment-modal/payment-modal.component';
import { NotesDetailModalComponent } from '../../../features/notes/components/notes-detail-modal/notes-detail-modal.component';
import { ModalService } from '../modal/modal.service';

@Injectable({
  providedIn: 'root',
})
export class PortfolioActionHandlerService implements OnDestroy {
  private quotationsService = inject(QuotationsService);
  private portfolioService = inject(PortfolioService);
  private paymentsPdfService = inject(PaymentsPdfService);
  private pdfGeneratorService = inject(PdfGeneratorService);
  private modalService = inject(ModalService);

  private actionCompletedSource = new Subject<string>();
  readonly actionCompleted$ = this.actionCompletedSource.asObservable();

  private pendingPaymentModals = new Set<number>();

  // Not a root-lifecycle no-op: actionCompletedSource is a real long-lived
  // stream, so this still needs to complete it if this service is ever torn
  // down (e.g. in a test). It just never fires from Angular itself, since
  // providedIn: 'root' services live for the app's lifetime.
  ngOnDestroy(): void {
    this.actionCompletedSource.complete();
  }

  // targetWindow: tab opened synchronously from the tap; closed if generation fails.
  async generateReport(
    quotation: IQuotation,
    open: boolean,
    targetWindow: Window | null = null,
  ): Promise<TCreatedPdf | undefined> {
    try {
      const fullQuotation = (await firstValueFrom(
        this.quotationsService.getQuotation(
          parseInt(quotation.id_factura!.toString(), 10),
        ),
      )) as IQuotation;

      const paymentsDescriptions = (fullQuotation.payment ?? []).map(
        (payment) => payment.description,
      );

      return await this.paymentsPdfService.createPaymentInvoicePdf(
        open,
        'Cancelled',
        fullQuotation,
        this.paymentsPdfService.getCompanyLogo(),
        paymentsDescriptions.join(', '),
        targetWindow,
      );
    } catch (error) {
      this.pdfGeneratorService.closePendingWindow(targetWindow);
      throw error;
    }
  }

  configPayments(
    quotation: IQuotation,
    initialTabId: string = PAYMENT_TAB_ID.REGISTER,
    invoiceVoided = false,
  ): void {
    const idFactura = quotation.id_factura;
    if (idFactura) {
      if (this.pendingPaymentModals.has(idFactura)) {
        return;
      }
      this.pendingPaymentModals.add(idFactura);
    }

    const releaseLock = () => {
      if (idFactura) {
        this.pendingPaymentModals.delete(idFactura);
      }
    };

    this.portfolioService
      .getPaymenInvoice(idFactura!)
      .pipe(take(1))
      .subscribe({
        next: (invoicePayments) => {
          this.modalService
            .openComponent(
              PaymentModalComponent,
              { variant: ModalVariant.DIALOG },
              {
                idFactura: quotation.id_factura,
                idCustomer: quotation.cliente.id_cliente,
                invoicePayments,
                initialTabId,
                invoiceVoided,
                onPaymentRegistered: () => {
                  this.actionCompletedSource.next('config-payments');
                },
                onClose: (result: {
                  sended: boolean;
                  refresh: boolean;
                  notifyState: number | null;
                }) => {
                  releaseLock();
                  if (result.refresh === true) {
                    this.actionCompletedSource.next('config-payments');
                  }

                  // One email per session, and only when a payment left the
                  // invoice partially or fully paid. Closing without
                  // registering anything, or after only deleting a payment,
                  // says nothing to the customer.
                  if (
                    result.notifyState === DOCUMENT_STATE.PARTIAL ||
                    result.notifyState === DOCUMENT_STATE.PAID
                  ) {
                    this.notifyPaymentState(quotation, result.notifyState);
                  }
                },
              },
            )
            .subscribe();
        },
        error: (error) => {
          releaseLock();
          console.error(
            `Error fetching payments for invoice ${idFactura}`,
            error,
          );
        },
      });
  }

  viewNotesDetail(
    quotation: IQuotation,
    notes: Note[],
    preloaded: {
      creditTotal: number;
      debitTotal: number;
      invoicePayments: IInvoicePayments[];
    },
  ): void {
    if (!quotation.id_factura) {
      return;
    }

    this.modalService
      .openComponent(
        NotesDetailModalComponent,
        { variant: ModalVariant.DIALOG },
        {
          idFactura: quotation.id_factura,
          notes,
          quotation,
          creditTotal: preloaded.creditTotal,
          debitTotal: preloaded.debitTotal,
          invoicePayments: preloaded.invoicePayments,
        },
      )
      .subscribe();
  }

  async handleAction(
    actionId: string,
    quotation: IQuotation,
    invoiceVoided = false,
  ): Promise<void> {
    switch (actionId) {
      case 'config-payments':
      case 'register-payment':
        this.configPayments(quotation, PAYMENT_TAB_ID.REGISTER, invoiceVoided);
        break;

      case 'generate-report':
        await this.generateReport(
          quotation,
          true,
          this.pdfGeneratorService.openPendingWindow(),
        );
        break;

      case 'view-invoice-detail-paid':
        this.configPayments(
          quotation,
          PAYMENT_TAB_ID.INVOICE_DETAIL,
          invoiceVoided,
        );
        break;
    }
  }

  /**
   * Emails the customer the state the invoice reached, carrying the same PDF
   * the receipt used to. Failures are logged, never surfaced: the payments
   * themselves are already registered and the user has closed the modal.
   */
  private notifyPaymentState(quotation: IQuotation, state: number): void {
    this.generateReport(quotation, false)
      .then((pdf) => {
        pdf?.getBase64((data: string) => {
          this.portfolioService
            .notifyInvoicePaymentState(quotation.id_factura!, state, data)
            .subscribe({
              error: (error) => {
                console.error('Error sending the payment state email', error);
              },
            });
        });
      })
      .catch((error) => {
        console.error('Error generating the payment state PDF', error);
      });
  }
}
